import { useState, useEffect } from 'react'
import { Search, Plus, UserCheck, UserX, X, Building2, Shield, Loader2, ChevronLeft, ChevronRight } from 'lucide-react'
import { medicalApi, usersApi } from '../../lib/api'

interface StaffItem {
  id: string
  account_id: string
  account_name?: string
  account_phone?: string
  name: string
  phone: string
  hospital_id: string
  hospital_name?: string
  medical_role: string
  status?: string
  created_at: string
}

interface UserItem {
  id: string
  account_id?: string
  member_no?: string
  name: string
  phone: string
  status: string
  role: string
}

const MedicalAccounts = () => {
  const currentUser = JSON.parse(localStorage.getItem('medicalUser') || '{}')
  const currentHospital = currentUser.hospital || '未知机构'
  const currentHospitalId = currentUser.hospital_id || ''

  const [accounts, setAccounts] = useState<StaffItem[]>([])
  const [loading, setLoading] = useState(true)
  const [searchTerm, setSearchTerm] = useState('')
  const [roleFilter, setRoleFilter] = useState('all')
  const [statusFilter, setStatusFilter] = useState('all')
  const [currentPage, setCurrentPage] = useState(1)
  const pageSize = 12

  const [showModal, setShowModal] = useState(false)
  const [modalType, setModalType] = useState<'add' | 'edit'>('add')
  const [selectedStaff, setSelectedStaff] = useState<StaffItem | null>(null)
  const [formLoading, setFormLoading] = useState(false)

  const [formData, setFormData] = useState({
    medical_role: 'staff'
  })

  const [confirmingAccount, setConfirmingAccount] = useState<StaffItem | null>(null)
  const [confirmType, setConfirmType] = useState<'freeze' | 'unfreeze' | 'logout' | 'activate'>('freeze')
  const [actionLoading, setActionLoading] = useState(false)

  // 用户搜索（添加模式）
  const [boundUserId, setBoundUserId] = useState('')
  const [boundUserInfo, setBoundUserInfo] = useState<{ name: string; phone: string; member_no?: string } | null>(null)
  const [userSearch, setUserSearch] = useState('')
  const [userResults, setUserResults] = useState<UserItem[]>([])
  const [showUserDropdown, setShowUserDropdown] = useState(false)

  const loadAccounts = async (page: number = 1) => {
    setLoading(true)
    try {
      const params: Record<string, unknown> = {
        page,
        page_size: pageSize,
      }
      if (searchTerm) params.keyword = searchTerm
      if (roleFilter !== 'all') params.medical_role = roleFilter

      const resp = await medicalApi.list(params as any) as any
      let items = resp.items || resp.staff || []

      // 前端过滤状态
      if (statusFilter !== 'all') {
        items = items.filter((s: StaffItem) => {
          if (statusFilter === 'active') return s.status === 'active'
          if (statusFilter === 'frozen') return s.status === 'frozen'
          if (statusFilter === 'inactive') return s.status === 'inactive'
          return true
        })
      }

      setAccounts(items)
      setTotal(resp.total || 0)
      setTotalPages(resp.total_pages || 1)
      setCurrentPage(resp.page || page)
    } catch (e) {
      console.error('Failed to load accounts', e)
    } finally {
      setLoading(false)
    }
  }

  const [total, setTotal] = useState(0)
  const [totalPages, setTotalPages] = useState(1)

  useEffect(() => {
    loadAccounts(1)
  }, [currentHospitalId, searchTerm, roleFilter, statusFilter])

  // 防抖搜索
  useEffect(() => {
    const t = setTimeout(() => setCurrentPage(1), 400)
    return () => clearTimeout(t)
  }, [searchTerm, roleFilter, statusFilter])

  // 用户搜索
  useEffect(() => {
    if (!userSearch || userSearch.length < 2) { setUserResults([]); return }
    const t = setTimeout(async () => {
      try {
        const res = await usersApi.list({ keyword: userSearch, page_size: 20 }) as { users: UserItem[] }
        // 过滤掉已是医护的用户
        const active = (res.users || []).filter((u: UserItem) => u.status === 'active')
        setUserResults(active)
      } catch { setUserResults([]) }
    }, 400)
    return () => clearTimeout(t)
  }, [userSearch])

  const handleConfirmAction = async () => {
    if (!confirmingAccount) return
    setActionLoading(true)
    try {
      if (confirmType === 'freeze') {
        await medicalApi.freeze(confirmingAccount.id)
      } else if (confirmType === 'unfreeze') {
        await medicalApi.unfreeze(confirmingAccount.id)
      } else if (confirmType === 'logout') {
        await medicalApi.delete(confirmingAccount.id)
      } else if (confirmType === 'activate') {
        await medicalApi.unfreeze(confirmingAccount.id)
      }
      setConfirmingAccount(null)
      loadAccounts(currentPage)
    } catch (e) {
      alert((e as Error).message)
    } finally {
      setActionLoading(false)
    }
  }

  const openAddModal = () => {
    setModalType('add')
    setFormData({ medical_role: 'staff' })
    setBoundUserId('')
    setBoundUserInfo(null)
    setUserSearch('')
    setUserResults([])
    setShowUserDropdown(false)
    setShowModal(true)
  }

  const openEditModal = (staff: StaffItem) => {
    setSelectedStaff(staff)
    setModalType('edit')
    setFormData({ medical_role: staff.medical_role })
    setBoundUserId('')
    setBoundUserInfo(null)
    setUserSearch('')
    setUserResults([])
    setShowUserDropdown(false)
    setShowModal(true)
  }

  const handleSubmit = async () => {
    if (modalType === 'add' && !boundUserId) { alert('请搜索选择要绑定的用户账号'); return }

    setFormLoading(true)
    try {
      if (modalType === 'add') {
        // 搜索用户添加模式：传 account_id，后端自动绑定本机构
        await medicalApi.create({
          account_id: boundUserId,
          medical_role: 'staff',  // 添加时固定为医护人员
        })
      } else if (selectedStaff) {
        await medicalApi.update(selectedStaff.id, {
          medical_role: formData.medical_role,
        })
      }
      setShowModal(false)
      loadAccounts(currentPage)
    } catch (e) {
      alert((e as Error).message)
    } finally {
      setFormLoading(false)
    }
  }

  const getBoundUserDisplay = () => {
    if (boundUserInfo) return boundUserInfo
    if (selectedStaff?.account_id === boundUserId) {
      return { name: selectedStaff.account_name || selectedStaff.name, phone: selectedStaff.account_phone || selectedStaff.phone }
    }
    return { name: '未知用户', phone: '-' }
  }

  const getRoleLabel = (role: string) => {
    switch (role) {
      case 'staff': return '医护人员'
      case 'supervisor': return '机构负责人'
      default: return role
    }
  }

  const getRoleBadgeColor = (role: string) => {
    switch (role) {
      case 'staff': return 'bg-blue-100 text-blue-700'
      case 'supervisor': return 'bg-purple-100 text-purple-700'
      default: return 'bg-gray-100 text-gray-700'
    }
  }

  const maskPhone = (p: string) => p ? p.replace(/(\d{3})\d{4}(\d{4})/, '$1****$2') : '-'

  const statusMap: Record<string, { label: string; color: string }> = {
    active: { label: '正常', color: 'bg-green-100 text-green-700' },
    frozen: { label: '冻结', color: 'bg-orange-100 text-orange-600' },
    inactive: { label: '注销', color: 'bg-gray-100 text-gray-500' },
  }

  return (
    <div className="space-y-6">
      {/* 页面标题 */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-800">医护管理</h1>
          <p className="text-gray-500 text-sm mt-1">共 {total} 位（本机构）</p>
        </div>
        <button onClick={openAddModal}
          className="flex items-center gap-2 bg-green-700 text-white px-4 py-2 rounded-xl hover:bg-green-800 transition-colors">
          <Plus className="w-4 h-4" /><span>添加人员</span>
        </button>
      </div>

      {/* 筛选和搜索 */}
      <div className="bg-white rounded-2xl shadow-md p-4">
        <div className="flex flex-col lg:flex-row gap-4">
          <div className="flex-1 relative">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
            <input
              type="text"
              placeholder="搜索姓名、手机号..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-12 pr-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-green-500"
            />
          </div>
          <div className="flex gap-3">
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="px-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-green-500"
            >
              <option value="all">全部状态</option>
              <option value="active">正常</option>
              <option value="frozen">冻结</option>
              <option value="inactive">注销</option>
            </select>
          </div>
        </div>
      </div>

      {/* 列表 */}
      {loading ? (
        <div className="flex justify-center py-20"><Loader2 className="w-8 h-8 animate-spin text-green-600" /></div>
      ) : (
        <>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {accounts.map((account) => {
              const st = statusMap[account.status || 'active'] || statusMap.active
              return (
                <div key={account.id} className="bg-white rounded-2xl shadow-md p-6 hover:shadow-lg transition-shadow">
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex items-center gap-3">
                      <div className="w-14 h-14 bg-gradient-to-br from-green-500 to-green-600 rounded-xl flex items-center justify-center">
                        <span className="text-white text-xl font-bold">
                          {(account.account_name || account.name || '?').charAt(0)}
                        </span>
                      </div>
                      <div>
                        <h3 className="font-bold text-gray-800">{account.account_name || account.name || '-'}</h3>
                        <span className={`px-2 py-0.5 text-xs font-medium rounded-full ${getRoleBadgeColor(account.medical_role)}`}>
                          {getRoleLabel(account.medical_role)}
                        </span>
                      </div>
                    </div>
                    <span className={`px-2 py-1 text-xs font-medium rounded-full ${st.color}`}>
                      {st.label}
                    </span>
                  </div>

                  <div className="space-y-2 mb-4 text-sm text-gray-600">
                    <div className="flex items-center gap-2">
                      <Building2 size={14} />
                      <span>{currentHospital}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Shield size={14} />
                      <span>{maskPhone(account.account_phone || account.phone)}</span>
                    </div>
                  </div>

                  <div className="flex gap-2 pt-3 border-t">
                    <button
                      onClick={() => {
                        setConfirmType(account.status === 'frozen' ? 'unfreeze' : 'freeze')
                        setConfirmingAccount(account)
                      }}
                      className={`flex-1 flex items-center justify-center gap-1 py-2 rounded-lg transition-colors text-sm ${
                        account.status === 'frozen'
                          ? 'text-green-600 hover:bg-green-50'
                          : 'text-orange-500 hover:bg-orange-50'
                      }`}>
                      {account.status === 'frozen' ? (
                        <><UserCheck size={14} /><span>解冻</span></>
                      ) : (
                        <><UserX size={14} /><span>冻结</span></>
                      )}
                    </button>
                    <button
                      onClick={() => {
                        setConfirmType(account.status === 'inactive' ? 'activate' : 'logout')
                        setConfirmingAccount(account)
                      }}
                      className={`flex-1 flex items-center justify-center gap-1 py-2 rounded-lg transition-colors text-sm ${
                        account.status === 'inactive'
                          ? 'text-green-600 hover:bg-green-50'
                          : 'text-red-600 hover:bg-red-50'
                      }`}>
                      {account.status === 'inactive' ? (
                        <><UserCheck size={14} /><span>激活</span></>
                      ) : (
                        <><UserX size={14} /><span>注销</span></>
                      )}
                    </button>
                  </div>
                </div>
              )
            })}
            {accounts.length === 0 && (
              <div className="col-span-full text-center py-20 text-gray-400">暂无数据</div>
            )}
          </div>

          {/* 分页 */}
          {totalPages > 1 && (
            <div className="flex items-center justify-center gap-2">
              <button onClick={() => loadAccounts(currentPage - 1)} disabled={currentPage <= 1}
                className="p-2 border rounded-lg disabled:opacity-40 hover:bg-gray-50">
                <ChevronLeft size={16} />
              </button>
              <span className="px-3 py-1 text-sm">{currentPage} / {totalPages}</span>
              <button onClick={() => loadAccounts(currentPage + 1)} disabled={currentPage >= totalPages}
                className="p-2 border rounded-lg disabled:opacity-40 hover:bg-gray-50">
                <ChevronRight size={16} />
              </button>
            </div>
          )}
        </>
      )}

      {/* 添加/编辑模态框 */}
      {showModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50" onClick={() => setShowModal(false)}>
          <div className="bg-white rounded-2xl w-full max-w-lg mx-4 shadow-2xl" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between px-6 py-4 border-b">
              <h3 className="text-lg font-bold text-gray-800">
                {modalType === 'add' ? '添加人员' : '编辑人员'}
              </h3>
              <button onClick={() => setShowModal(false)} className="p-2 hover:bg-gray-100 rounded-lg">
                <X size={20} />
              </button>
            </div>

            <div className="p-6 space-y-4">
              {/* 添加模式：搜索绑定用户 */}
              {modalType === 'add' && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    绑定用户账号 <span className="text-red-500">*</span>
                  </label>
                  {boundUserId ? (
                    <div className="flex items-center justify-between p-3 bg-green-50 border-2 border-green-200 rounded-xl">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-green-600 rounded-full flex items-center justify-center">
                          <span className="text-white font-bold text-sm">
                            {getBoundUserDisplay().name.charAt(0)}
                          </span>
                        </div>
                        <div>
                          <p className="font-semibold text-gray-800">
                            {getBoundUserDisplay().name}
                            {getBoundUserDisplay().member_no && <span className="ml-1 text-xs text-gray-400">({getBoundUserDisplay().member_no})</span>}
                          </p>
                          <p className="text-xs text-gray-500">{getBoundUserDisplay().phone}</p>
                        </div>
                      </div>
                      <button
                        onClick={() => { setBoundUserId(''); setBoundUserInfo(null) }}
                        className="p-1.5 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-lg">
                        <X size={16} />
                      </button>
                    </div>
                  ) : (
                    <div className="relative">
                      <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
                      <input
                        type="text"
                        placeholder="搜索用户姓名或手机号..."
                        value={userSearch}
                        onChange={(e) => {
                          setUserSearch(e.target.value)
                          setShowUserDropdown(e.target.value.length > 0)
                        }}
                        className="w-full pl-12 pr-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-green-500"
                      />
                      {showUserDropdown && userResults.length > 0 && (
                        <div className="absolute z-10 w-full mt-1 bg-white border border-gray-200 rounded-xl shadow-lg max-h-48 overflow-y-auto">
                          {userResults.map((u) => (
                            <button
                              key={u.id}
                              onClick={() => {
                                setBoundUserId(u.account_id || u.id)
                                setBoundUserInfo({ name: u.name, phone: u.phone, member_no: u.member_no })
                                setUserSearch('')
                                setShowUserDropdown(false)
                              }}
                              className="w-full flex items-center gap-3 p-3 hover:bg-green-50 transition-colors text-left">
                              <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center">
                                <span className="text-green-700 font-bold text-xs">{u.name?.charAt(0) || '?'}</span>
                              </div>
                              <div>
                                <p className="font-medium text-gray-800 text-sm">{u.name} {u.member_no && <span className="text-xs text-gray-400">({u.member_no})</span>}</p>
                                <p className="text-xs text-gray-400">{u.phone}</p>
                              </div>
                            </button>
                          ))}
                        </div>
                      )}
                      {showUserDropdown && userResults.length === 0 && (
                        <div className="absolute z-10 w-full mt-1 bg-white border border-gray-200 rounded-xl shadow-lg p-4 text-center text-gray-400 text-sm">
                          未找到匹配的用户
                        </div>
                      )}
                    </div>
                  )}
                  <p className="text-xs text-amber-600 mt-1">请相关用户完成小程序登录注册后，再进行搜索绑定医护权限</p>
                </div>
              )}

              {/* 编辑模式：显示当前用户信息 */}
              {modalType === 'edit' && selectedStaff && (
                <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-xl">
                  <div className="w-10 h-10 bg-green-600 rounded-full flex items-center justify-center">
                    <span className="text-white font-bold text-sm">
                      {(selectedStaff.account_name || selectedStaff.name || '?').charAt(0)}
                    </span>
                  </div>
                  <div>
                    <p className="font-semibold text-gray-800">{selectedStaff.account_name || selectedStaff.name}</p>
                    <p className="text-xs text-gray-500">{currentHospital}</p>
                  </div>
                </div>
              )}

              {modalType === 'add' && (
                <div className="bg-gray-50 rounded-xl p-4 space-y-2">
                  <div className="flex items-center space-x-2 text-sm">
                    <Building2 className="w-4 h-4 text-gray-400" />
                    <span className="text-gray-500">所属机构：</span>
                    <span className="font-medium text-gray-800">{currentHospital}</span>
                  </div>
                  <div className="flex items-center space-x-2 text-sm">
                    <Shield className="w-4 h-4 text-gray-400" />
                    <span className="text-gray-500">角色：</span>
                    <span className="font-medium text-gray-800">医护人员</span>
                  </div>
                </div>
              )}
            </div>

            <div className="flex items-center justify-end gap-3 px-6 py-4 border-t">
              <button onClick={() => setShowModal(false)}
                className="px-6 py-2 border border-gray-200 rounded-xl hover:bg-gray-50 transition-colors">
                取消
              </button>
              <button onClick={handleSubmit} disabled={formLoading}
                className="px-6 py-2 bg-green-700 text-white rounded-xl hover:bg-green-800 transition-colors disabled:opacity-50">
                {formLoading ? '提交中...' : modalType === 'add' ? '添加' : '保存'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 确认弹窗 */}
      {confirmingAccount && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="bg-white rounded-2xl w-full max-w-sm mx-4 shadow-2xl p-6">
            <div className="text-center">
              <div className={`w-16 h-16 mx-auto rounded-full flex items-center justify-center mb-4 ${
                confirmType === 'logout' || confirmType === 'freeze' ? 'bg-red-100' : 'bg-green-100'
              }`}>
                {confirmType === 'logout' || confirmType === 'freeze'
                  ? <UserX className="w-8 h-8 text-red-600" />
                  : <UserCheck className="w-8 h-8 text-green-600" />}
              </div>
              <h3 className="text-lg font-bold text-gray-800 mb-2">
                {confirmType === 'freeze' && '确认冻结'}
                {confirmType === 'unfreeze' && '确认解冻'}
                {confirmType === 'logout' && '确认注销'}
                {confirmType === 'activate' && '确认激活'}
              </h3>
              <p className="text-gray-500 text-sm mb-6">
                确定要{confirmType === 'freeze' ? '冻结' : confirmType === 'unfreeze' ? '解冻' : confirmType === 'logout' ? '注销' : '激活'}账号 <strong>{confirmingAccount.name}</strong> 吗？
                {confirmType === 'freeze' && '冻结后该账号将无法登录。'}
                {confirmType === 'logout' && '注销后该账号将无法恢复使用。'}
              </p>
              <div className="flex gap-3">
                <button onClick={() => setConfirmingAccount(null)}
                  className="flex-1 py-2.5 border border-gray-200 rounded-xl text-gray-600 hover:bg-gray-50 font-medium">
                  取消
                </button>
                <button onClick={handleConfirmAction}
                  className={`flex-1 py-2.5 rounded-xl text-white font-medium ${
                    confirmType === 'logout' || confirmType === 'freeze'
                      ? 'bg-red-600 hover:bg-red-700'
                      : 'bg-green-600 hover:bg-green-700'
                  }`}>
                  {actionLoading ? '处理中...' : '确认'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default MedicalAccounts